import { ThemeEvents } from '@theme/events';
import { getPaidQuantities } from '@theme/bfcm-limits';

/**
 * BFCM limit messages, "You're saving" / "Free gift" / "Shipping" rows above the Rebuy Smart Cart subtotal.
 *
 * Renders into <div id="mj-cart-totals">, a Custom Code block placed in the Smart Cart builder.
 * Theme settings > Cart > "Hide shipping line until free shipping is reached" hides the shipping row until it's FREE.
 */

/** @returns {any} */
const smartCart = () => window.Rebuy?.SmartCart;

/**
 * Promo code amount Rebuy subtracts on top of cart.total_price when codes aren't applied to the
 * Shopify cart (mirrors SmartCart.subtotalNumber, so savings stay in step with the subtotal shown).
 * @returns {number}
 */
function rebuyCodeDiscount() {
  const cart = smartCart();
  if (cart.hasStorefrontDiscounts || cart.useDiscountCodeMessageOnly() || !cart.shouldApplyDiscountCode()) return 0;
  return cart.getCalculatedCartDiscount() > 0 ? cart.discount?.amount || 0 : 0;
}

/** @type {Map<string, Promise<any>>} */
const products = new Map();

/**
 * Variant compare-at price in cents (0 when unset). cart.js doesn't include it, so read the product JSON once per handle.
 * @param {any} item - Shopify cart line
 * @returns {Promise<number>}
 */
async function compareAtPrice(item) {
  if (!products.has(item.handle)) {
    const request = fetch(`${window.Shopify?.routes?.root ?? '/'}products/${item.handle}.js`)
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    products.set(item.handle, request);
  }
  const product = await products.get(item.handle);
  if (!product) products.delete(item.handle); // retry on the next render
  return product?.variants.find((/** @type {any} */ v) => v.id === item.variant_id)?.compare_at_price ?? 0;
}

/**
 * @param {any} cart - Shopify cart JSON
 * @returns {Promise<{ discounts: number, gift: number }>} amounts in cents
 */
async function getSavings(cart) {
  const compareAtPrices = await Promise.all(cart.items.map(compareAtPrice));
  let priceSavings = 0;
  let gift = 0;

  cart.items.forEach((/** @type {any} */ item, /** @type {number} */ index) => {
    // Any line discounted to $0 is a gift (BOGO kit, progress-bar gifts, ...)
    if (item.final_line_price === 0) {
      gift += item.original_line_price;
      return;
    }
    // Regular price: compare-at (sale) or the one-time price (subscriptions), whichever is higher.
    // item.original_price is what's actually charged before discounts, so sale + subscription savings land here once.
    const oneTimePrice = item.selling_plan_allocation?.compare_at_price ?? item.original_price;
    const regularPrice = Math.max(compareAtPrices[index], oneTimePrice);
    priceSavings += (regularPrice - item.original_price) * item.quantity;
  });

  // total_discount holds line + order discounts (including the gifts' 100% off) but not sale or selling plan
  // pricing, which is already baked into item.original_price — so nothing is counted twice.
  return { discounts: priceSavings + cart.total_discount - gift, gift };
}

/** @returns {boolean} */
function hasFreeShipping() {
  const cart = smartCart();
  const tier = cart?.progressBar?.tiers?.find((/** @type {any} */ t) => t.type === 'shipping');
  // Same check the progress bar uses: threshold, currency rate, exclusions, free shipping on subscriptions
  return Boolean(tier && cart.progressTierReached(tier));
}

/**
 * Rebuy's "+" can't be disabled from the theme (its limit helpers are internal), so at a limit the checkout
 * validation rejects the change with a 422 and these messages explain why the quantity didn't move.
 * @param {any} cart - Shopify cart JSON
 * @returns {string[]}
 */
function getLimitMessages(cart) {
  const limits = Theme.bfcmLimits;
  if (!limits) return [];

  const { byVariant, total } = getPaidQuantities(cart);
  const messages = [];
  if ([...byVariant.values()].some((quantity) => quantity >= limits.perSku)) messages.push(limits.skuMessage);
  if (total > limits.perOrder) messages.push(limits.orderMessage);
  return messages;
}

let latestRender = 0;

/** @param {any} cart - Shopify cart JSON */
async function render(cart) {
  const container = document.getElementById('mj-cart-totals');
  if (!container || !smartCart() || !cart?.items || typeof cart.total_discount !== 'number') return;

  const renderId = ++latestRender;
  const { discounts, gift } = await getSavings(cart);
  if (renderId !== latestRender) return; // a newer cart arrived while fetching compare-at prices

  const format = (/** @type {number} */ cents) => smartCart().formatMoney(cents);
  const saving = discounts + rebuyCodeDiscount();
  const freeShipping = hasFreeShipping();

  /** @type {[string, string][]} */
  const rows = [];
  if (saving > 0) rows.push(['You’re saving', format(saving)]);
  if (gift > 0) rows.push(['Free gift value', format(gift)]);
  if (cart.item_count && (freeShipping || !Theme.rebuyCart?.hideShippingUntilFree)) {
    rows.push(['Shipping', freeShipping ? 'FREE' : 'Calculated at checkout']);
  }

  container.innerHTML = rows
    .map(
      ([label, value]) =>
        `<div class="mj-cart-totals__row"><span>${label}</span><span>${value}</span></div>`
    )
    .join('');

  // Merchant copy from the metafield: set as text, never as HTML
  container.prepend(
    ...getLimitMessages(cart).map((message) => {
      const limitMessage = document.createElement('p');
      limitMessage.className = 'mj-cart-totals__limit';
      limitMessage.textContent = message;
      return limitMessage;
    })
  );
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => render(/** @type {any} */ (event).detail?.resource));
document.addEventListener('rebuy:smartcart.show', () => render(window.Rebuy?.Cart?.cart));
